export type SetupFamily = 'self_service' | 'partner' | 'managed';
export type SetupPhase = 'credentials' | 'verifying' | 'verified' | 'saving' | 'complete';

const MANAGED_PREFIXES = ['aws_'];
const MANAGED_KEYS = new Set([
  'face_recognition', 'stripe', 'telnyx_cellular', 'telnyx_sms', 'telnyx_voice',
  'livekit', 'livekit-ingress',
]);

export function apiOriginForHostname(hostname: string): string {
  return hostname.startsWith('shadow.') ? 'https://shadow.api.intrlock.io' : 'https://api.intrlock.io';
}

export function classifyIntegration(definition: { integration_key: string; status?: string }): SetupFamily {
  if (definition.status === 'partner_required') return 'partner';
  if (MANAGED_KEYS.has(definition.integration_key) || MANAGED_PREFIXES.some(prefix => definition.integration_key.startsWith(prefix))) return 'managed';
  return 'self_service';
}

/** Only integrations a customer can authorize themselves belong in the setup catalog. */
export function isCustomerConfigurableIntegration(definition: { integration_key: string; status?: string }): boolean {
  return classifyIntegration(definition) === 'self_service';
}

export function setupProgressIndex(phase: SetupPhase, hasScope: boolean): number {
  if (phase === 'complete') return 3;
  if (phase === 'verified' || phase === 'saving') return 2;
  if (phase === 'verifying') return 1;
  return hasScope ? 1 : 0;
}

export const providerPreparation: Record<string, string[]> = {
  schlage: ['Use the property owner’s Schlage Home account.', 'The lock must already be online in Schlage Home. Supported cloud-connected models are discovered after authorization.'],
  pdk: ['Sign in with a PDK account that can access the systems you manage.', 'After login, select one or more discovered systems and assign each one to its Intrlock property. No System ID entry is required.'],
  brivo: ['Create a dedicated Brivo API application or credential with access only to the required account.', 'Have the client ID, client secret, API key, and account ID available. Provider account tier and scopes require confirmation in Brivo.'],
  turing: ['Create an API access token in Turing Vision.', 'Confirm the account can see the exact sites and cameras that will be assigned to this property.'],
  unifi_protect: ['Create a Site Manager API key as the console owner.', 'Confirm Protect and Remote Access are online. Intrlock will ask you to select a console when the key exposes more than one.'],
  unifi_access: ['This connector currently requires direct Access-console API reachability.', 'Remote Cloud Connector authorization is not yet certified; verify network routing and TLS before setup.'],
  controlbyweb: ['Use a dedicated least-privilege controller account.', 'Confirm the controller has a stable address and is reachable from the approved Intrlock bridge/network path.'],
  onvif: ['Confirm the camera is ONVIF-enabled and reachable from the approved Intrlock bridge/network path.', 'Use a dedicated read-only camera account where supported.'],
};

export const verifiedProviderEndpoints: Record<string, Array<{ label: string; url: string }>> = {
  schlage: [{ label: 'Registered callback URL', url: 'https://api.intrlock.io/schlage/callback' }],
  pdk: [{ label: 'Required PDK callback URL', url: 'https://api.intrlock.io/pdk/callback' }],
};

export function readinessLabel(family: SetupFamily): string {
  if (family === 'partner') return 'Partner approval required';
  if (family === 'managed') return 'Managed by Intrlock';
  return 'Self-service setup';
}

export async function syncTuringCamerasAfterEnable({
  apiBase,
  credentialId,
  propertyId,
  headers,
  request = fetch,
}: {
  apiBase: string;
  credentialId: string;
  propertyId: string;
  headers: Record<string, string>;
  request?: typeof fetch;
}): Promise<any> {
  if (!credentialId) throw new Error('Turing setup did not return a credential identifier.');
  if (!propertyId) throw new Error('A property is required before Turing cameras can be imported.');
  const response = await request(`${apiBase}/integrations/turing/systems/${encodeURIComponent(credentialId)}/sync-cameras`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ property_ids: [propertyId], primary_property_id: propertyId }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || data.message || `Turing camera import failed (${response.status}).`);
  return data;
}
